import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { AuthorizationService } from '../authorization/authorization.service';
import { ExecutiveAnalyticsResponse } from '../../shared/models/executiveAnalyticsResponse';

@Component({
  selector: 'div.dtf-executiveanalyticspage',
  standalone: true,
  templateUrl: './executiveanalytics.component.html',
  styleUrls: ['./executiveanalytics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExecutiveAnalyticsComponent implements OnInit {
  private readonly auth = inject(AuthorizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly countFormatter = new Intl.NumberFormat('en-US');
  isLoading = true;
  loadError = '';

  readonly metrics = [
    { label: 'Active Website Users', value: 'Loading...' },
    { label: 'Active Website Accounts', value: 'Loading...' },
    { label: 'Active Website Customers', value: '12.7K' },
    { label: 'Active Portfolio Accounts', value: '12580' },
    { label: 'Active Portfolio Customers', value: '22.7K' },
    { label: `Payoff Quotes (${new Date().getFullYear()})`, value: 'Loading...' },
    { label: 'Payments', value: '10,040' },
    { label: 'Payment Amount', value: '$37.5M' }
  ];

  // Bar lengths reproduce the mockup's illustrative indicators, not payment shares.
  readonly payments = [
    { label: 'ACH', value: '$18.6M', width: 72 },
    { label: 'Card', value: '$4.8M', width: 36 },
    { label: 'Wire', value: '$12.4M', width: 55 }
  ];

  readonly assets = [
    { label: '1 asset', percentage: 42 },
    { label: '2-3 assets', percentage: 36 },
    { label: '4-5 assets', percentage: 14 },
    { label: '6+ assets', percentage: 8 }
  ];

  readonly selfService = [
    { label: 'Amortizations', value: '2,146' },
    { label: 'Invoice Downloads', value: '18,920' },
    { label: 'Title Copies', value: '1,078' },
    { label: `Payoff Quotes (${new Date().getFullYear()})`, value: 'Loading...' }
  ];

  ngOnInit(): void {
    this.loadAnalytics();
  }

  loadAnalytics(): void {
    this.isLoading = true;
    this.loadError = '';
    this.updateLiveCounts('Loading...', 'Loading...', 'Loading...');

    this.auth.getExecutiveAnalytics().pipe(
      map(response => {
        if (!response || ![
          response.activeUserCount,
          response.activeAccountCount,
          response.payoffCountCurrentYear
        ].every(count => Number.isSafeInteger(count) && count >= 0)) {
          throw new Error('Executive analytics response contains invalid counts.');
        }
        return response;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response: ExecutiveAnalyticsResponse) => {
        this.updateLiveCounts(
          this.countFormatter.format(response.activeUserCount),
          this.countFormatter.format(response.activeAccountCount),
          this.countFormatter.format(response.payoffCountCurrentYear)
        );
        this.isLoading = false;
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.updateLiveCounts('Unavailable', 'Unavailable', 'Unavailable');
        this.loadError = 'Unable to load executive analytics. Please try again.';
        this.isLoading = false;
        this.changeDetector.markForCheck();
      }
    });
  }

  private updateLiveCounts(users: string, accounts: string, payoffs: string): void {
    for (const metric of this.metrics) {
      if (metric.label === 'Active Website Users') {
        metric.value = users;
      } else if (metric.label === 'Active Website Accounts') {
        metric.value = accounts;
      } else if (metric.label === `Payoff Quotes (${new Date().getFullYear()})`) {
        metric.value = payoffs;
      }
    }
    for (const service of this.selfService) {
      if (service.label === `Payoff Quotes (${new Date().getFullYear()})`) {
        service.value = payoffs;
      }
    }
  }
}
